"use strict";

const noiseGLSL = `
// Description : Array and textureless GLSL 2D/3D/4D simplex noise functions.
//      Author : Ian McEwan, Ashima Arts.
//  Maintainer : stegu
//     Lastmod : 20110822 (ijm)
//     License : Copyright (C) 2011 Ashima Arts. All rights reserved.
//               Distributed under the MIT License. See LICENSE file.
//               https://github.com/ashima/webgl-noise
//               https://github.com/stegu/webgl-noise

vec3 mod289(vec3 x) {
	return x - floor(x * (1.0 / 289.0)) * 289.0;
}

vec4 mod289(vec4 x) {
	return x - floor(x * (1.0 / 289.0)) * 289.0;
}

vec4 permute(vec4 x) {
		 return mod289(((x*34.0)+1.0)*x);
}

vec4 taylorInvSqrt(vec4 r) {
	return 1.79284291400159 - 0.85373472095314 * r;
}

float snoise(vec3 v) {
	const vec2  C = vec2(1.0/6.0, 1.0/3.0) ;
	const vec4  D = vec4(0.0, 0.5, 1.0, 2.0);

	vec3 i  = floor(v + dot(v, C.yyy) );
	vec3 x0 =   v - i + dot(i, C.xxx) ;

	vec3 g = step(x0.yzx, x0.xyz);
	vec3 l = 1.0 - g;
	vec3 i1 = min( g.xyz, l.zxy );
	vec3 i2 = max( g.xyz, l.zxy );

	vec3 x1 = x0 - i1 + C.xxx;
	vec3 x2 = x0 - i2 + C.yyy; // 2.0*C.x = 1/3 = C.y
	vec3 x3 = x0 - D.yyy;      // -1.0+3.0*C.x = -0.5 = -D.y

	i = mod289(i);
	vec4 p = permute( permute( permute(
						 i.z + vec4(0.0, i1.z, i2.z, 1.0 ))
					 + i.y + vec4(0.0, i1.y, i2.y, 1.0 ))
					 + i.x + vec4(0.0, i1.x, i2.x, 1.0 ));

	float n_ = 0.142857142857; // 1.0/7.0
	vec3  ns = n_ * D.wyz - D.xzx;

	vec4 j = p - 49.0 * floor(p * ns.z * ns.z);  //  mod(p,7*7)

	vec4 x_ = floor(j * ns.z);
	vec4 y_ = floor(j - 7.0 * x_ );    // mod(j,N)

	vec4 x = x_ *ns.x + ns.yyyy;
	vec4 y = y_ *ns.x + ns.yyyy;
	vec4 h = 1.0 - abs(x) - abs(y);

	vec4 b0 = vec4( x.xy, y.xy );
	vec4 b1 = vec4( x.zw, y.zw );

	vec4 s0 = floor(b0)*2.0 + 1.0;
	vec4 s1 = floor(b1)*2.0 + 1.0;
	vec4 sh = -step(h, vec4(0.0));

	vec4 a0 = b0.xzyw + s0.xzyw*sh.xxyy ;
	vec4 a1 = b1.xzyw + s1.xzyw*sh.zzww ;

	vec3 p0 = vec3(a0.xy,h.x);
	vec3 p1 = vec3(a0.zw,h.y);
	vec3 p2 = vec3(a1.xy,h.z);
	vec3 p3 = vec3(a1.zw,h.w);

	vec4 norm = taylorInvSqrt(vec4(dot(p0,p0), dot(p1,p1), dot(p2, p2), dot(p3,p3)));
	p0 *= norm.x;
	p1 *= norm.y;
	p2 *= norm.z;
	p3 *= norm.w;

	vec4 m = max(0.6 - vec4(dot(x0,x0), dot(x1,x1), dot(x2,x2), dot(x3,x3)), 0.0);
	m = m * m;
	return 42.0 * dot( m*m, vec4( dot(p0,x0), dot(p1,x1),
																dot(p2,x2), dot(p3,x3) ) );
	}
`;

const vertexShader = `
attribute vec4 position;
void main() {
	gl_Position = position;
}
`;

const fragmentShader = `
precision mediump float;

uniform sampler2D u_texture;
uniform vec2 u_texture_dim;
uniform vec2 u_head_pos;
uniform vec3 u_multipliers;
uniform float u_time;
uniform vec2 u_resolution;
uniform vec2 u_mouse;
uniform float u_scroll;
uniform vec3 u_bg;

${noiseGLSL}

const vec3 blue = vec3(0.38, 0.25, 0.92);
const vec3 red = blue.bgr;
const vec3 green = red.grb;

float getVal(vec2 pos) {
	float newY = pos.y * 3. + u_time / 5. - u_scroll * 1.2;
	float noise = snoise(vec3(pos.x * 2., newY, u_time / 6.)); // (-1, 1)
	noise = (noise + 1.) / 2.;
	return noise;
}

float triangleDistance(in vec2 p) {
		const float k = sqrt(3.0);
		p.x = abs(p.x) - 1.0;
		p.y = p.y + 1.0/k;
		if( p.x+k*p.y>0.0 ) p = vec2(p.x-k*p.y,-k*p.x-p.y)/2.0;
		p.x -= clamp( p.x, -2.0, 0.0 );
		return -length(p)*sign(p.y);
}

// signed distance to a 2D triangle
float sdTriangle( in vec2 p, in vec2 p0, in vec2 p1, in vec2 p2 )
{
	vec2 e0 = p1 - p0;
	vec2 e1 = p2 - p1;
	vec2 e2 = p0 - p2;

	vec2 v0 = p - p0;
	vec2 v1 = p - p1;
	vec2 v2 = p - p2;

	vec2 pq0 = v0 - e0*clamp( dot(v0,e0)/dot(e0,e0), 0.0, 1.0 );
	vec2 pq1 = v1 - e1*clamp( dot(v1,e1)/dot(e1,e1), 0.0, 1.0 );
	vec2 pq2 = v2 - e2*clamp( dot(v2,e2)/dot(e2,e2), 0.0, 1.0 );
		
	float s = e0.x*e2.y - e0.y*e2.x;
	vec2 d = min( min( vec2( dot( pq0, pq0 ), s*(v0.x*e0.y-v0.y*e0.x) ),
		vec2( dot( pq1, pq1 ), s*(v1.x*e1.y-v1.y*e1.x) )),
		vec2( dot( pq2, pq2 ), s*(v2.x*e2.y-v2.y*e2.x) ));

	return -sqrt(d.x)*sign(d.y);
}

vec2 getDisplacement(in float rand) {
	vec2 res = vec2(
		getVal(vec2(rand)),
		getVal(vec2(rand + 5.3))
	) * 2. - 1.;
	return res * 0.1;
}

void main() {
	float ar = u_resolution.x / u_resolution.y;
	vec2 pos = gl_FragCoord.xy / u_resolution;
	pos.y /= ar;
	vec2 mouse = u_mouse / u_resolution;
	mouse.y = (1. - mouse.y) / ar;

	vec2 circleCenter = vec2(0.5 - 0.02, 0.5 / ar - 0.09);

	float td = sdTriangle(
		pos,
		// mix(mouse, circleCenter - vec2(0., 0.3), 0.8),
		circleCenter - vec2(0., 0.15),
		circleCenter + vec2(-0.15, 0.),
		circleCenter + vec2(0.15, 0.)
	);

	td = clamp(0., 1., 1. - td);
	td = 1. - pow(td, 5.);
	float distToTriangle = 1. - td;

	float distToMouse = distance(mouse, pos) / length(vec2(1., 1. / ar)); // [0, 1]
	distToMouse = clamp(distToMouse, 0., 1.);
	distToMouse = pow(1. - distToMouse, 3.);

	float distToCenter = 1. - distance(pos, circleCenter) / distance(vec2(0., circleCenter.y), circleCenter); // [0, 1]
	// distToCenter = clamp(distToCenter * 5., 0., 1.);
	distToCenter = 1. - pow(1. - distToCenter, 0.7);
	float noise = clamp(getVal(pos), 0., 1.) * 0.8; // [0, 1]

	float noiseMultiplier = u_multipliers.x;
	float mouseMultiplier = u_multipliers.y / 1.9;
	float triangleMultiplier = u_multipliers.z;
	float centerMultiplier = 1.;

	centerMultiplier -= triangleMultiplier / 1.5;

	float val = (noise * noiseMultiplier + distToMouse * mouseMultiplier + distToTriangle * triangleMultiplier) / (noiseMultiplier + mouseMultiplier + triangleMultiplier) + distToCenter * centerMultiplier;

	// val = distToTriangle;

	const float discardThreshold = 0.72;
	float alpha = smoothstep(discardThreshold - 0.005, discardThreshold, val);

	const float scale = 1.7;
	const float ytrans = 0.2;

	vec2 headCenter = vec2(0.5, 0.5 / ar);
	vec2 currentHeadCenter = headCenter;
	currentHeadCenter.x = (u_head_pos.x / u_texture_dim.x - 0.5) / scale + 0.5;
	currentHeadCenter.y = (1. - u_head_pos.y / u_texture_dim.y - ytrans) / scale / (u_texture_dim.x / u_texture_dim.y);

	vec2 tdiff = currentHeadCenter - headCenter;
	float texposx = (pos.x - 0.5) * scale + 0.5;
	float texposy = pos.y * scale * u_texture_dim.x / u_texture_dim.y + ytrans;
	vec2 texpos = vec2(texposx, texposy) + tdiff;
	vec4 texcolor = texture2D(u_texture, texpos);

	vec3 c = mix(blue, green, noise * 0.4 + (1. - distToMouse) * 0.2);
	vec4 color = mix(vec4(c, alpha), texcolor, texcolor.a);

	if (pos.y < headCenter.y - 0.1 && alpha < 0.1) {
		color.a = 0.;
	}

	if (alpha == 0. && val + 0.04 > discardThreshold && val + 0.04 < discardThreshold + 0.01 && (texcolor.a < 0.1 || texposy < 0.5)) {
		color.a = smoothstep(discardThreshold - 0.02, discardThreshold, val + 0.04);

		vec3 r = vec3(0.89, 0.15, 0.07);
		vec3 b = vec3(0.067, 0.4, 0.867);
		vec3 g = vec3(0.133, 0.733, 0.267);
		vec3 c2 = (r * u_multipliers.r + g * u_multipliers.g + b * u_multipliers.b) * 1.3;

		color.rgb = mix(c2, color.rgb, texcolor.a);
	}

	gl_FragColor = color;
	// gl_FragColor = vec4(mix(vec3(val), color.rgb, 0.2), 1.);
}
`;

// let scene, camera, renderer, clock, dpr;
let canvas, gl, programInfo, bufferInfo, dpr;
let uniforms = {
	u_resolution: [0, 0],
	u_mouse: [0, 0],
	u_bg: [0.098, 0.098, 0.102],
	u_time: 0,
	u_multipliers: [0, 0, 0],
	u_texture_dim: [0, 0],
	u_head_pos: [0, 0],
	u_texture: null,
	u_scroll: scrollY,
};

function onResize() {
	let w = window.innerWidth / 2;
	let h = window.innerHeight;
	dpr = window.devicePixelRatio || 1;

	canvas.width = w * dpr;
	canvas.height = h * dpr;

	uniforms.u_resolution = [w * dpr, h * dpr];

	gl.viewport(0, 0, gl.canvas.width * dpr, gl.canvas.height * dpr);
}

function setup3d() {
	canvas = document.createElement("canvas");
	canvas.classList.add("webgl");
	document.body.appendChild(canvas);
	gl = canvas.getContext("webgl", {
		antialias: false,
		alpha: true,
		premultipliedAlpha: false,
	});

	const textures = twgl.createTextures(gl, {
		thing: {
			src: "/assets/images/me_transparent.png",
			flipY: true,
			// src: "/assets/images/allrgb-teaser.jpg",
		},
	});
	uniforms.u_texture = textures.thing;
	uniforms.u_texture_dim = [639, 732];
	uniforms.u_head_pos = [339, 248];

	programInfo = twgl.createProgramInfo(gl, [vertexShader, fragmentShader]);
	bufferInfo = twgl.createBufferInfoFromArrays(gl, {
		position: [-1, -1, 0, 1, -1, 0, -1, 1, 0, -1, 1, 0, 1, -1, 0, 1, 1, 0],
	});

	// update the renderer dimensions on window resize
	window.addEventListener("resize", onResize, false);
	onResize();

	const setDarkTheme = () => {
		uniforms.u_bg = isDarkTheme ? [0.098, 0.098, 0.102] : [1, 1, 1];
	};
	darkModeEventListeners.push(setDarkTheme);
	setDarkTheme();

	setupTernarySlider();

	requestAnimationFrame(update);
}

function setupTernarySlider() {
	const r = 12;
	const width = 500;
	const height = (width * Math.sqrt(3)) / 2;
	const svg = document.querySelector(".slider");

	let points = [
		[width / 2, 0],
		[width, height],
		[0, height],
	];

	const triangle = svg.querySelector("polygon");
	const circle = svg.querySelector("circle.control");
	const barycentricLines = svg.querySelectorAll(".barycentric line");
	const outerLines = svg.querySelectorAll(".outer line");
	const valueTexts = document.querySelectorAll(".percent");

	function limit(t) {
		const min = 1e-5;
		const max = 1 - min;
		return t < min ? min : t > max ? max : t;
	}

	function lerp(p1, p2, a) {
		return p1.map((x, i) => x + (p2[i] - x) * a);
	}

	function updateValue(a, b, c) {
		uniforms.u_multipliers = [a, b, c];
		function toText(n) {
			n = Math.round(n * 100) + '%';
			if (n === '100%') {
				n = '100';
			}
			return `(${n.padStart(3, ' ')})`;
		}
		valueTexts[0].innerText = toText(a);
		valueTexts[1].innerText = toText(b);
		valueTexts[2].innerText = toText(c);
	}

	// function intersection([x1, y1], [x2, y2], [x3, y3], [x4, y4]) {
	// 	const D = (x1 - x2) * (y3 - y4) - (y1 - y2) * (x3 - x4);
	// 	const Px =
	// 		((x1 * y2 - y1 * x2) * (x3 - x4) - (x1 - x2) * (x3 * y4 - y3 * x4)) / D;
	// 	const Py =
	// 		((x1 * y2 - y1 * x2) * (y3 - y4) - (y1 - y2) * (x3 * y4 - y3 * x4)) / D;
	// 	return [Px, Py];
	// }

	function toAbc(x, y) {
		let [q, r] = points[0];
		let [s, t] = points[1];
		let [u, v] = points[2];

		// from WolframAlpha: `solve for a and b: q * a + s * b + u * (1 - a - b) = x, r * a + t * b + v * (1 - a - b) = y`
		let a =
			(-s * v + s * y + t * u - t * x - u * y + v * x) /
			(-q * t + q * v + r * s - r * u - s * v + t * u);
		let b =
			(q * (v - y) + r * (x - u) + u * y - v * x) /
			(q * (v - t) + r * (s - u) - s * v + t * u);

		a = limit(a);
		b = limit(b);
		let c = limit(1 - a - b);

		x = q * a + s * b + u * c;
		y = r * a + t * b + v * c;

		return [a, b, c, x, y];
	}

	function updateTriangle() {
		let p = points.map((xy) => xy.join(",")).join(" ");
		triangle.setAttribute("points", p);
		for (let n of [0, 1, 2]) {
			outerLines[n].setAttribute("x1", points[(n + 2) % 3][0]);
			outerLines[n].setAttribute("y1", points[(n + 2) % 3][1]);
		}
		updatePoint();
	}

	function updatePoint(mx, my) {
		mx ||= +circle.getAttribute("cx");
		my ||= +circle.getAttribute("cy");
		const abc = toAbc(mx, my);
		mx = abc[3];
		my = abc[4];

		updateValue(abc[0], abc[1], abc[2]);

		circle.setAttribute("cx", mx);
		circle.setAttribute("cy", my);

		for (let n of [0, 1, 2]) {
			const l = lerp(points[(n + 2) % 3], points[n], abc[n]);
			outerLines[n].setAttribute("x2", l[0]);
			outerLines[n].setAttribute("y2", l[1]);

			barycentricLines[n].setAttribute("x1", mx);
			barycentricLines[n].setAttribute("y1", my);
			// const bp = intersection(
			// 	[mx, my],
			// 	points[n],
			// 	points[(n + 1) % 3],
			// 	points[(n + 2) % 3]
			// );
			const bp = l;
			barycentricLines[n].setAttribute("x2", bp[0]);
			barycentricLines[n].setAttribute("y2", bp[1]);
		}
	}

	function svgCoordinates(evt) {
		const clientX = evt.clientX || evt.targetTouches[0].clientX;
		const clientY = evt.clientY || evt.targetTouches[0].clientY;
		const ctm = svg.getScreenCTM(); // matrix that converts client coordinates to SVG coordinates
		const x = (clientX - ctm.e) / ctm.a;
		const y = (clientY - ctm.f) / ctm.d;
		return [x, y];
	}
	function moveTo(evt) {
		const [mx, my] = svgCoordinates(evt);
		const cx = +circle.getAttribute("cx");
		const cy = +circle.getAttribute("cy");
		const isHovering = Math.sqrt((cx - mx) ** 2 + (cy - my) ** 2) < r;
		circle.classList.toggle("highlight", isHovering);
		if (isDragging) {
			updatePoint(mx, my);
		}
	}

	let isDragging = false;
	function mousedown(evt, isMouse) {
		isDragging = true;
		isMouse && evt.preventDefault();
		moveTo(evt);
		circle.classList.add("highlight");
	}
	function mousemove(evt, isMouse) {
		isMouse && evt.preventDefault();
		moveTo(evt);
	}
	function mouseup(evt, isMouse) {
		if (!isDragging) return;
		isMouse && evt.preventDefault();
		isDragging = false;
	}
	function mouseleave(evt, isMouse) {
		if (!isDragging) return;
		isMouse && evt.preventDefault();
		isDragging = false;
		circle.classList.remove("highlight");
	}
	triangle.addEventListener("mousedown", (evt) => mousedown(evt, true));
	triangle.addEventListener("touchstart", (evt) => mousedown(evt, true), { passive: false });
	triangle.addEventListener("mousemove", (evt) => mousemove(evt, true));
	triangle.addEventListener("touchmove", (evt) => mousemove(evt, true), { passive: false });
	triangle.addEventListener("mouseup", (evt) => mouseup(evt, true));
	triangle.addEventListener("mouseleave", (evt) => mouseleave(evt, true));
	triangle.addEventListener("touchend", (evt) => mouseleave(evt, false));
	triangle.addEventListener("touchcancel", (evt) => mouseleave(evt, false));

	updateTriangle();
}

let LOG_FPS = false;
let prevTime = 0;
let fps = 0;

function update(time) {
	requestAnimationFrame(update);

	if (LOG_FPS) {
		fps = Math.round(1000 / (time - prevTime));
		prevTime = time;
		console.log(fps);
	}

	uniforms.u_time = time / 1000;
	uniforms.u_scroll = scrollY;
	// uniforms.u_mouse[0] = uniforms.u_resolution[0] / 2;
	uniforms.u_mouse[0] = mouse.x * dpr;
	uniforms.u_mouse[1] = mouse.y * dpr;

	gl.useProgram(programInfo.program);
	twgl.setBuffersAndAttributes(gl, programInfo, bufferInfo);
	twgl.setUniforms(programInfo, uniforms);
	twgl.drawBufferInfo(gl, bufferInfo);
}
